import {
  createRootRouteWithContext,
  Outlet,
  useNavigate,
  useLocation,
  useRouterState,
} from "@tanstack/react-router";
import React from "react";
// eslint-disable-next-line
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { IAuthContext, useAuth } from "../contexts/AuthContext";
import { Navbar } from "@/components/layout/Navbar";

const PUBLIC_PATHS = ["/", "/login", "/login-link-modal", "/login-link"];
/** The landing page and the books' title screens bring their own layout. */
const isBarePath = (pathname: string) =>
  pathname === "/" || pathname.startsWith("/books/");

export const Route = createRootRouteWithContext<{ auth: IAuthContext }>()({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: RootComponent, // eslint-disable-line  @typescript-eslint/no-use-before-define
});

function RootComponent() {
  const { isLoggedIn, loading } = useAuth();
  const navigate = useNavigate({ from: Route.fullPath });
  const location = useLocation();
  const routerState = useRouterState();
  const search = Route.useSearch();

  React.useEffect(() => {
    if (loading) return;

    const shouldRedirectToHome = isLoggedIn && location.pathname === "/login";
    const shouldRedirectToLogin =
      !isLoggedIn &&
      !PUBLIC_PATHS.includes(location.pathname) &&
      !isBarePath(location.pathname);

    if (shouldRedirectToHome) {
      const to = search.redirect || "/home";
      navigate({ to, replace: true });
    }
    if (shouldRedirectToLogin) {
      navigate({
        to: "/login",
        replace: true,
        search: {
          redirect: routerState.location.href,
        },
      });
    }
  }, [
    isLoggedIn,
    loading,
    location.pathname,
    navigate,
    routerState.location.href,
    routerState.location.pathname,
    search.redirect,
  ]);

  const isBare = isBarePath(location.pathname);
  const isFullWidthRoute = location.pathname === "/home";
  // The game is full screen; its own menu has home and log out.
  const showNavbar = location.pathname !== "/play";

  return (
    <div className="bg-secondary-950 min-h-screen">
      {isBare ? (
        <Outlet />
      ) : (
        <>
          {showNavbar && <Navbar />}
          <div
            className={
              isFullWidthRoute
                ? "max-w-[100vw] mx-auto px-[1.5rem]"
                : "max-w-[100rem] mx-auto px-[1.5rem]"
            }
          >
            <Outlet />
          </div>
        </>
      )}
      {import.meta.env.VITE_ENVIRONMENT === "local" && (
        <TanStackRouterDevtools />
      )}
    </div>
  );
}
